/**
 * Read More Component
 * Handles collapsible text with line-clamping
 */
class ReadMore extends HTMLElement {
  constructor() {
    super();
    this.button = null;
    this.content = null;
    this.readMoreText = null;
    this.readLessText = null;
    this.icon = null;
    this.isExpanded = false;
    this.lineClampClasses = [];
  }

  connectedCallback() {
    this.content = this.querySelector('[data-read-more-content]');
    this.button = this.querySelector('[data-read-more-button]');
    
    if (!this.content || !this.button) return;

    this.readMoreText = this.button.querySelector('[data-read-more]');
    this.readLessText = this.button.querySelector('[data-read-less]');
    this.icon = this.button.querySelector('.read-more-icon');
    this.lineClampClasses = this.getLineClampClasses();

    // Check if content needs read more (immediate, no delay)
    this.checkOverflow();
    
    // Handle button click
    this.button.addEventListener('click', () => this.toggle());
    
    // Recheck on window resize (only if collapsed)
    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        if (!this.isExpanded) this.checkOverflow();
      }, 150);
    });
  }

  checkOverflow() {
    if (this.lineClampClasses.length === 0) return;

    // Get clamped height first
    const clampedHeight = this.content.clientHeight;
    
    // Temporarily remove line-clamp to get full content height
    this.removeLineClampClasses();
    const fullHeight = this.content.scrollHeight;
    
    // Re-add line-clamp
    this.addLineClampClasses();

    // Check if content is being cut off
    const isOverflowing = fullHeight > clampedHeight;

    // Show/hide button based on overflow
    if (isOverflowing) {
      this.button.classList.remove('hidden');
    } else {
      this.button.classList.add('hidden');
    }
  }

  getLineClampClasses() {
    const classes = Array.from(this.content.classList);
    return classes.filter(cls => cls.includes('line-clamp-'));
  }

  removeLineClampClasses() {
    if (this.lineClampClasses.length === 0) return;
    this.content.classList.remove(...this.lineClampClasses);
  }

  addLineClampClasses() {
    if (this.lineClampClasses.length === 0) return;
    this.content.classList.add(...this.lineClampClasses);
  }

  toggle() {
    this.isExpanded = !this.isExpanded;

    // Get current height
    const startHeight = this.content.clientHeight;
    
    if (this.isExpanded) {
      // Expand - remove line clamp to get full height
      this.removeLineClampClasses();
      const endHeight = this.content.scrollHeight;
      
      // Animate height
      this.animateHeight(startHeight, endHeight);
      
      // Update button text and icon
      if (this.readMoreText) this.readMoreText.classList.add('hidden');
      if (this.readLessText) this.readLessText.classList.remove('hidden');
      if (this.icon) this.icon.style.transform = 'rotate(180deg)';
      this.button.setAttribute('aria-expanded', 'true');
    } else {
      // Collapse - add line clamp back
      this.addLineClampClasses();
      const endHeight = this.content.clientHeight;
      
      // Animate height
      this.animateHeight(startHeight, endHeight);
      
      // Update button text and icon
      if (this.readMoreText) this.readMoreText.classList.remove('hidden');
      if (this.readLessText) this.readLessText.classList.add('hidden');
      if (this.icon) this.icon.style.transform = 'rotate(0deg)';
      this.button.setAttribute('aria-expanded', 'false');
    }
  }

  animateHeight(startHeight, endHeight) {
    // Set explicit start height
    this.content.style.height = `${startHeight}px`;
    this.content.style.overflow = 'hidden';
    
    // Force reflow
    this.content.offsetHeight;
    
    // Apply transition and end height
    this.content.style.transition = 'height 0.3s ease-in-out';
    this.content.style.height = `${endHeight}px`;
    
    // Clean up after transition
    const cleanup = () => {
      this.content.style.height = '';
      this.content.style.overflow = '';
      this.content.style.transition = '';
      this.content.removeEventListener('transitionend', cleanup);
    };
    
    this.content.addEventListener('transitionend', cleanup);
  }
}

customElements.define('read-more', ReadMore);
